import { useApi, useAuth } from '../../hooks';
import { useQuery } from '@tanstack/react-query';
import { cleanMeetingNotes } from '../../components/HealthCoachDashboard/utils';
import { computeMeetingsToDisplay } from '../../components/UserDashboard/utils';
import { ChartNoteStatus, MeetingNote } from '../types';

const USER_MEETINGS_POLL_INTERVAL_MS = 30_000;

// Poll while any past visit awaiting completion still has an unresolved
// transcript (no status yet, or still transcribing), so the visit-note CTA
// flips to "ready" promptly. Scoped to the "to be completed" past visits so we
// don't poll indefinitely on future / never-transcribed meetings.
const isAwaitingTranscript = (meetings?: MeetingNote[]) => {
  const { toBeCompleted } = computeMeetingsToDisplay(meetings ?? []);

  return toBeCompleted.some((meeting) => {
    const status = meeting.chart_notes?.[0]?.status;
    return status == null || status === ChartNoteStatus.TranscriptStarted;
  });
};

export const useUserMeetings = (userId: string) => {
  const { api } = useApi();
  const { isAuthenticated, loading, userToken } = useAuth();

  const fetchUserMeetings = async () => {
    const requestObj = {
      route: `/private/dashboard/user/${userId}/meetings`,
      token: userToken,
    };

    const meetings = await api(requestObj);

    return meetings.map(cleanMeetingNotes);
  };

  const { data, isInitialLoading, isLoading, isError } = useQuery<
    MeetingNote[],
    Error
  >(['hc-user-meetings', userToken, userId], () => fetchUserMeetings(), {
    enabled: isAuthenticated && !loading && !!userToken,
    refetchOnWindowFocus: false,
    refetchInterval: (data) =>
      isAwaitingTranscript(data) ? USER_MEETINGS_POLL_INTERVAL_MS : false,
  });

  return {
    meetings: data,
    isInitialLoading,
    isLoading,
    isError,
  };
};
